import { IDictionary } from 'util/domain';
import {
	ClientLinkShare,
	IShareService, 
	IInheritedShareableStore, 
	IShareServiceFactory,
	ServerLinkShare,
	Activity,
	Library
} from 'sharebase/domain';
import {
	IFolderService,
	IDocumentService
} from 'sharebase/services';
import {
	Acl,
	ObjectType,
	PermissionType,
	ShareEntity,
	RightsType,
	AdvancedOption,
	RightsOption,
	ShareBaseFolder,
	ShareBaseDocument,
	ShareBaseItem,
	pxItemTypeGuards as typeGuard,
	isPurgedOrPurging,
	PhoenixActivity
} from 'phoenix/domain';
import { ToolbarService } from 'sharebase-app/services/appToolBarService';
import { AclDatastore } from 'sharebase/dataStores/aclDatastore';
import {
	FolderDetails,
	DefaultFolderDetails
} from 'sharebase-app/components/folderDetails/folderDetails';
import { ISortContentFunction } from 'sharebase/filters/sortContent';
/**
* @ngdoc service
* @name  sharebase-app.sbaFolderDataStore
* 
* @description 
* A datastore used to interact with the contents of a Folder.
* 
**/
export class FolderDataStore implements IInheritedShareableStore {
	public static $inject = [
		'$q',
		'pxAuthService',
		'sbSortContentFilter',
		'sbShareServiceFactory',
		'sbFolderService',
		'sbDocumentService',
		'sbaLibraryDataStore',
		'sbaToolbarService',
		'sbAclDatastore',
		'sbFolderDetailService'
	]

	constructor(
		private $q:ng.IQService, 		
		private pxAuthService,
		private sortContent:ISortContentFunction,
		private sbShareServiceFactory:IShareServiceFactory, 
		private sbFolderService:IFolderService, 
		private sbDocumentService:IDocumentService, 
		private sbaLibraryDataStore, 
		private sbaToolbarService:ToolbarService, 
		private sbAclDatastore:AclDatastore,
		private sbFolderDetailService,
	) {
		this.shareService = this.sbShareServiceFactory.create(this.sbFolderService, this);
	}

	public shareService: IShareService;
	public shares:Acl[] = [];
	public inheritedShares:Acl[] = [];
	public links:ClientLinkShare[] = [];
	public inheritedLinks = [];
	public type = ObjectType.FOLDER;

	private _cachedPromises:IDictionary<ng.IPromise<any>>= {
		activities: null,
		load: null,
		notificationSettings: null,
		details: null
	};

	private folderId:number;

	private _details:FolderDetails;
	public get details() {
		return this._details;
	}

	private _folder:ShareBaseFolder;
	public get folder() {
		return this._folder;
	}

	/**
	* @ngdoc property
	* @name sbaFolderDataStore#folders
	* @propertyOf sharebase-app.sbaFolderDataStore
	* @kind property
	*
	* @description
	* A given folder's subfolders           
	*
	* @returns {object} An array of a selected folder's subfolders
	*/
	private _subFolders:ShareBaseFolder[] = [];
	public get folders() {
		return this._subFolders;
	}
	public set folders(folderArray) {
		this._subFolders = folderArray;
		this.allContentDirty = true;
	}

	/**
	* @ngdoc property
	* @name sbaFolderDataStore#documents
	* @propertyOf sharebase-app.sbaFolderDataStore
	* @kind property
	*
	* @description
	* A given folder's documents
	* 
	* @returns {array} An array of a selected folder's documents
	*/
	public _documents:ShareBaseDocument[] = [];
	public get documents() {
		return this._documents;
	}
	public set documents(docArray) {
		this._documents = docArray;
		this.allContentDirty = true;
	}

	/**
	* @ngdoc property
	* @name sbaFolderDataStore#allContent
	* @propertyOf sharebase-app.sbaFolderDataStore
	* @kind property
	*
	* @description
	* A given folder's subfolders and documents           
	*
	* @returns {object} An array of a selected folder's subfolders and documents
	*/
	private allContentDirty:boolean;
	private _allContent:ShareBaseItem[] = [];
	public get allContent() {
		if(this.allContentDirty === true) {
			this._allContent = (this._subFolders as ShareBaseItem[]).concat(this._documents);
			this.allContentDirty = false;
		}
		return this._allContent;
	}

	/**
	* @ngdoc property
	* @name sbaFolderDataStore#breadcrumbs
	* @propertyOf sharebase-app.sbaFolderDataStore
	* @kind property
	*
	* @description
	* A given folder's path           
	*
	* @returns {object} An array of folders
	*/
	private _breadcrumbs:(ShareBaseFolder|Library)[] = [];
	public get breadcrumbs() {
		return this._breadcrumbs;
	}

	/**
	* @ngdoc property
	* @name sbaFolderDataStore#isSharedContent
	* @propertyOf sharebase-app.sbaFolderDataStore
	* @kind property
	*
	* @description
	* Whether or not the folder is shared with the current user.        
	*
	* @returns {boolean} Flag determining if the folder is shared.
	*/
	private _isSharedContent = false;
	public get isSharedContent() {
		return this._isSharedContent;
	}

	/**
	* @ngdoc method
	* @name sbaFolderDataStore#load
	* @methodOf sharebase-app.sbaFolderDataStore
	* @kind function
	*
	* @description
	* Loads or refreshes a selected folder
	* 
	* @param {object} folderId A Folder ID
	*
	* @returns {Deferred} A promise that will resolve with a selected folder
	*/
	public load(folderId:number, sortContents:boolean = true, forceLoad:boolean = false) {
		//possible to still pass in a string through directives, etc.
		//Value could be techincally be 'recents', 'favorites'
		folderId = +folderId;

		if(this._cachedPromises.load && folderId === this.folderId && forceLoad === false) {
			return this._cachedPromises.load;
		}
		else {
			this.clearStore();
			this.folderId = folderId;

			let libPromise = this.$q.resolve();

			if (!this.pxAuthService.isShareByLinkUser) {
				// File history got messed up during 1.7.1 forward integration,
				// these changes were made as part of SBWEB-1189. Loading the library
				// is used to determine if the loaded folder is shared content or not.
				// Only load personal library if the user is not a share by link user,
				// otherwise the API sends an Unauthorized error and the app will redirect
				// to the login page
				libPromise = !this.sbaLibraryDataStore.myLibrary ?
						this.sbaLibraryDataStore.loadLibrary() : 
						this.$q.resolve(this.sbaLibraryDataStore.myLibrary);
			}

			return this._cachedPromises.load = libPromise
				.then(() => {
					return this.sbFolderService.getFolder(folderId);
				})			
				.then(folder => {
					this._folder = folder;					
					this._breadcrumbs = folder.Embedded.Path ? folder.Embedded.Path : null;
					return this.getCurrentUserAcl();
				})
				.then(acl => {		
					if(!acl.hasPermission(PermissionType.LISTFOLD)) {
						return this.$q.all([
							[] as ShareBaseFolder[],
							[] as ShareBaseDocument[]
						])
					} 
					
					return this.$q.all([
						this.sbFolderService.getSubFolders(this._folder), 
						this.sbFolderService.getDocuments(this._folder)
					]);
				})
				.then(responses => {					
					this.folders = responses[0].filter(sf => !isPurgedOrPurging(sf));
					this.documents = responses[1].filter(doc => !isPurgedOrPurging(doc));
				})
				.then(() => {	
					if(sortContents){
						this.sort(this.sbaToolbarService.sortType);
					}
					
					// check to see if the folder is in user's library
					this._isSharedContent = this.pxAuthService.isShareByLinkUser || this.sbaLibraryDataStore.myLibrary.LibraryId !== this._folder.LibraryId;

					return this._folder;
				});
		}
	}

	public loadDetails(forceLoad:boolean = false) {
		this.guardFolder();

		if(this._cachedPromises.details && forceLoad === false){
			return this._cachedPromises.details;
		}
		else {
			return this._cachedPromises.details = this.sbFolderDetailService.getFolderDetails(this._folder)
				.then(details => {
					this._details = {
						dateCreated: details.dateCreated,
						dateModified: details.dateModified,
						owner: details.owner,
						totalFiles: details.totalFiles,
						activities: []
					};

					return this._details;
				});
		}
	}

	public loadActivities(forceLoad:boolean = false) {
		this.guardFolder();

		if(this._cachedPromises.activities && forceLoad === false){
			return this._cachedPromises.activities;
		}
		else {
			return this._cachedPromises.activities = this.sbFolderDetailService
				.getFolderActivities(this.folder)
				.then((activities:PhoenixActivity[]) => {
					return this._details.activities = activities.map(activity => new Activity(activity));
				});	
		}
	}

	/**
	 * @ngdoc method
	 * @name sbaFolderDataStore#moveFolder
	 * @propertyOf sharebase-app.sbaFolderDataStore
	 * @kind method
	 *
	 * @description
	 * Move a specific folder to a new location.
	 *
	 * @param {Object|Object[]} source The folder or collection of folders being moved
	 * @param {Object} destination The location to move the folder to, can
	 * be either a library or another folder.
	 * @returns {Deferred|Deferred[]} A single promise or collection of promises that
	 * will resolve to an Angular HTTP response.
	 */
	public moveFolder(sources: ShareBaseFolder|ShareBaseFolder[], destination) {
		if (!Array.isArray(sources)) {
			sources = [sources];
		}

		return this.$q.all(sources.map(source => {
			return this.sbFolderService
				.moveFolder(source, destination)
				.then(res => {
					if (this.folders) {
						this.folders = this.folders.filter(subFold => {
							return subFold.FolderId !== source.FolderId;
						});
					}
					return res;
				});
		}))
		.then(res => {
			this.details.dateModified = new Date();
		});
	}

	public renameItem(item:ShareBaseFolder|ShareBaseDocument, name:string){
		if(typeGuard.isShareBaseDocument(item)){
			return this.renameDocument(item, name);
		}
		else if(typeGuard.isShareBaseFolder(item)){
			return this.renameFolder(item, name);
		}
	}
	
	/**
	* @ngdoc method
	* @name sbaFolderDataStore#renameFolder
	* @methodOf sharebase-app.sbaFolderDataStore
	* @kind function
	*
	* @description
	* Updates the folders name
	* 
	* @param {object} folder A Folder resource
	* @param {object} name A new name for the folder
	*
	* @returns {Deferred} A promise that will resolve with a renamed folder
	*/
	private renameFolder(folder:ShareBaseFolder, name:string){
		return this.sbFolderService.renameFolder(folder, name)
			.then(renamedfolder => {
				if(this._folder && this.folders){
					//figure out if there is a DS object to update
					// - is the folder the root DS folder item
					var dsSubfolderIndex = -1;
					var dsFolderObj = this._folder.FolderId === folder.FolderId ? this._folder : null;
					
					// - is the folder a sub folder of the DS folder item
					if(!dsFolderObj){
						var dsSubFolderObj = this.folders
							.filter(function(f){ 
								return f.FolderId === folder.FolderId;
							});

						if(dsSubFolderObj.length === 1){	
							dsSubfolderIndex = this.folders.indexOf(dsSubFolderObj[0]);
						}
					}
					//update datastore
					if(dsFolderObj){
						//update root DS folder object
						this._folder.FolderName = renamedfolder.FolderName;
					}
					else if(dsSubfolderIndex > -1){
						//update sub folder object
						this.folders[dsSubfolderIndex].FolderName = renamedfolder.FolderName;
						this.folders[dsSubfolderIndex].IsFavorite = folder.IsFavorite;
					}
				}
				
				this.details.dateModified = new Date();

				return folder;
			});
	}

	/**
	 * @ngdoc method
	 * @name sbaFolderDataStore#renameDocument
	 * @methodOf sharebase-app.sbaFolderDataStore
	 * @kind function
	 * 
	 * @description
	 * Updates a document's name.
	 * 
	 * @param {integer} documentId The document's ID
	 * @param {string} name The new name for the document
	 * 
	 * @returns {Deferred} A promise that will resolve with a renamed document
	 */
	private renameDocument(document:ShareBaseDocument, name:string) {
		return this.sbDocumentService.renameDocument(document, name)
			.then(renamedDocument => {
				this.documents = this.documents.map(doc => {
					if (doc.DocumentId === renamedDocument.DocumentId) {
						doc.DocumentName = renamedDocument.DocumentName;
						doc.IsFavorite = document.IsFavorite;
						return angular.copy(doc)
					} else {
						return doc;
					}
				});
				this.sort(this.sbaToolbarService.sortType);
				return document;
			});
	}			

	/**
	* @ngdoc method
	* @name sbaFolderDataStore#createFolder
	* @methodOf sharebase-app.sbaFolderDataStore
	* @kind function
	*
	* @description
	* Create a sub foder
	* 
	* @param {object} folder A Folder resource
	* @param {object} name A name for the new folder
	*
	* @returns {Deferred} A promise that will resolve with a new folder
	*/
	public createFolder(name){
		return this.sbFolderService.createFolder(name, this._folder)
			.then(newFolder => {
				this.folders.push(newFolder);
				this.details.dateModified = new Date();
				this.sort(this.sbaToolbarService.sortType);
				
				return newFolder;
			});
	}

	public createDocument(name){
		return this.sbDocumentService
			.createDocument(name, this.folder)
			.then(doc => {
				this.documents.push(doc);
				this.sort(this.sbaToolbarService.sortType);
				return doc;
			});
	}

	/**
	 * @ngdoc method
	 * @name sbaFolderDataStoreDataStore#moveDocument
	 * @methodOf sharebase-app.sbaFolderDataStore
	 * @kind function
	 *
	 * @description
	 * Move a document to a new location.
	 * 
	 * @param {Object} source The document to move.
	 * @param {Object} target The target folder to move the document to.
	 * @returns {Deferred|Deferred[]} A single promise or collection of promises that will resolve to an Angular HTTP response.
	 */
	public moveDocument(source:ShareBaseDocument|ShareBaseDocument[], target:ShareBaseFolder) {
		if (!Array.isArray(source)) {
			source = [source];
		} 

		var pendingDocs = source.map(src => {
			return this.sbDocumentService
				.moveDocument(src, target)
				.then(res => {
					if (this.documents) {
						this.documents = this.documents.filter(function(doc) {
							return doc.DocumentId !== src.DocumentId;
						});
					}
					return res;
				});
		});

		return this.$q.all(pendingDocs)
			.then(res => {
				this.updateTotalFileCount(-pendingDocs.length);
			});
	}

	public deleteFolder(folder, purge:boolean = false) {
		return this.sbFolderService.deleteFolder(folder, purge)
			.then(res => {
				if (this.folders) {
					this.folders = this.folders.filter(fold => {
						return fold.FolderId !== folder.FolderId;
					});
				}
				this.details.dateModified = new Date();
				this.sort(this.sbaToolbarService.sortType);
				return res;
			});
	}

	public deleteDocument(document, purge:boolean = false) {
		return this.sbDocumentService.deleteDocument(document, purge)
			.then(res => {
				this.documents = this.documents.filter(doc => {
					return doc.DocumentId !== document.DocumentId;
				});
				this.updateTotalFileCount(-1);
				this.sort(this.sbaToolbarService.sortType);
				return res;
			});
	}

	public clearStore() {
		this.documents = this.folders = [];
		this._folder = this.folderId = null;
		this._details = DefaultFolderDetails;
		this._isSharedContent = false;
		Object.keys(this._cachedPromises).forEach(k => {
			this._cachedPromises[k] = null;
		});
	}

	/**
	 * @ngdoc method
	 * @name sbaFolderDataStore#sort
	 * @methodOf sharebase-app.sbaFolderDataStore
	 * @kind function
	 * 
	 * @description
	 * Sort folder content based on provided sort parameters
	 * 
	 * @param {Object} sortType Parameters that determine how to sort the data
	 */
	public sort(sortType) {
		this.folders = this.sortContent(this.folders, sortType.type, sortType.ascending) as ShareBaseFolder[];
		this.documents = this.sortContent(this.documents, sortType.type, sortType.ascending) as ShareBaseDocument[];
	}

	public getPermissions(share:Acl){
		return this.getCurrentUserAcl().then(myAcl => this.shareService.getRights(myAcl, share));			
	}

	public getCurrentUserAcl(): ng.IPromise<Acl> {
		this.guardFolder();
		return this.sbAclDatastore.load(this._folder);
	}

	public getShares() {
		this.guardFolder();
		return this.shareService.getShares(this._folder);
	}

	public getLinks() {
		this.guardFolder();
		return this.shareService.getLinks(this._folder);
	}

	public createShare(entity: ShareEntity, right:RightsType, advanced:AdvancedOption[]) {
		this.guardFolder();
		return this.shareService.createShare(this._folder, entity, right, advanced);
	}

	public createShares(entities:ShareEntity[], right:RightsType, advanced:AdvancedOption[]){
		this.guardFolder();
		return this.shareService.createShares(this._folder, entities, right, advanced);
	}

	public updateShare(updatedShare:Acl, right:RightsType, advanced:AdvancedOption[]){
		this.guardFolder();
		return this.shareService.updateShare(this._folder, updatedShare, right, advanced);
	}

	public deleteShare(deletedShare:Acl): ng.IPromise<Acl[]> {
		this.guardFolder();
		return this.shareService.deleteShare(this._folder, deletedShare);
	}

	public createLink(share:ServerLinkShare, right:RightsOption) {	
		this.guardFolder();	
		return this.shareService.createLink(this._folder, share, right);
	}

	public revokeLink(revokedLink:ClientLinkShare){
		if(!revokedLink.Links.RevokeUri){ 
			throw new Error('Invalid link object.');
		}

		return this.shareService.revokeLink(revokedLink).then(() => revokedLink);
	}

	public getLinkRights(userAcl:Acl) {
		return this.shareService.getLinkRights(userAcl);
	}

	public messageSharedUsers(subject:string, message:string) {
		return this.shareService.sendShareMessage(this._folder, subject, message);
	}

	public updateTotalFileCount(increment:number = 1) {
		if(this.details != null) {
			this.details.totalFiles += increment;
			this.details.dateModified = new Date();
		}
	}
	
	private guardFolder() {
		if(!angular.isDefined(this._folder)){
			throw new Error('No folder loaded.');
		}
	}
}


// WEBPACK FOOTER //
// ./src/sharebase-app/dataStores/folderDataStore.ts